import { Suspense, lazy, useEffect } from "react";
import { Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { initTelegram, getTg } from "./lib/telegram.js";
import { api } from "./lib/api.js";
import { WelcomeScreen } from "./screens/WelcomeScreen.js";
import { OnboardingScreen } from "./screens/OnboardingScreen.js";
import { HomeScreen } from "./screens/HomeScreen.js";
import { OrdersScreen } from "./screens/OrdersScreen.js";
import { ProfileScreen } from "./screens/ProfileScreen.js";
import { ProfileEditScreen } from "./screens/ProfileEditScreen.js";
import { MyQrScreen } from "./screens/MyQrScreen.js";
import { OrderDetailScreen } from "./screens/OrderDetailScreen.js";
import { AppGate } from "./components/AppGate.js";

// Тяжёлые экраны лениво — они тянут большие компоненты,
// которые юзеру на /welcome ни к чему.
// Shop = новая «Упаковка» (магазин расходников). См. PACKING_REDESIGN_PLAN.md.
const ShopCatalogScreen = lazy(() =>
  import("./screens/ShopCatalogScreen.js").then((m) => ({ default: m.ShopCatalogScreen })),
);
const ShopCategoryScreen = lazy(() =>
  import("./screens/ShopCategoryScreen.js").then((m) => ({ default: m.ShopCategoryScreen })),
);
const ShopCartScreen = lazy(() =>
  import("./screens/ShopCartScreen.js").then((m) => ({ default: m.ShopCartScreen })),
);
const ShopCheckoutScreen = lazy(() =>
  import("./screens/ShopCheckoutScreen.js").then((m) => ({ default: m.ShopCheckoutScreen })),
);
// MVP упаковки (2026-05-15): cart → manager-handoff (без БД-заказа).
// Полный checkout-flow отложен; компоненты не удалены, роуты ниже комментарием.
const ShopManagerHandoffScreen = lazy(() =>
  import("./screens/ShopManagerHandoffScreen.js").then((m) => ({ default: m.ShopManagerHandoffScreen })),
);
const ShopOrderScreen = lazy(() =>
  import("./screens/ShopOrderScreen.js").then((m) => ({ default: m.ShopOrderScreen })),
);
const FlexParcelFormScreen = lazy(() =>
  import("./screens/FlexParcelFormScreen.js").then((m) => ({ default: m.FlexParcelFormScreen })),
);
const FlexCisFormScreen = lazy(() =>
  import("./screens/FlexCisFormScreen.js").then((m) => ({ default: m.FlexCisFormScreen })),
);
const FlexAttachmentsScreen = lazy(() =>
  import("./screens/FlexAttachmentsScreen.js").then((m) => ({ default: m.FlexAttachmentsScreen })),
);
const PreorderFillScreen = lazy(() =>
  import("./screens/PreorderFillScreen.js").then((m) => ({ default: m.PreorderFillScreen })),
);
const TemplatesListScreen = lazy(() =>
  import("./screens/TemplatesListScreen.js").then((m) => ({ default: m.TemplatesListScreen })),
);

function ScreenFallback() {
  return (
    <div className="app-shell">
      <div className="screen pad">
        <p className="t-body">Загрузка…</p>
      </div>
    </div>
  );
}

type ShopPaymentInfoForStart = {
  method?: "on_pickup" | "remote";
  paid: number;
  status: string;
};

export function shopPayStartTarget(orderId: string, info: ShopPaymentInfoForStart): string {
  if (
    info.status === "paid" ||
    info.status === "ready" ||
    info.status === "completed" ||
    info.method === "on_pickup"
  ) return `/shop/order/${orderId}`;
  const step = info.status === "awaiting_payment" && info.paid > 0 ? "receipt" : "requisites";
  return `/shop/checkout?orderId=${encodeURIComponent(orderId)}&step=${step}`;
}

async function handleStartParam(nav: ReturnType<typeof useNavigate>) {
  const startParam = getTg()?.initDataUnsafe?.start_param as string | undefined;
  if (!startParam) return;
  if (startParam.startsWith("pay_shop_")) {
    const orderId = startParam.slice("pay_shop_".length);
    if (!orderId) return;
    try {
      const info = await api<ShopPaymentInfoForStart>(`/shop/orders/${orderId}/payment-info`);
      nav(shopPayStartTarget(orderId, info), { replace: true });
    } catch {
      nav(`/shop/checkout?orderId=${encodeURIComponent(orderId)}&step=requisites`, { replace: true });
    }
    return;
  }
  // Бывший Ozon/WB pay-deep-link (startapp=pay_<id> → /orders/:id/pay) удалён
  // вместе с PaymentScreen — модуль Ozon/WB в архиве (archive/ozonwb-flow).
  if (startParam.startsWith("order_qr_")) {
    const orderId = startParam.slice("order_qr_".length);
    if (orderId) nav(`/orders/${orderId}`, { replace: true });
    return;
  }
  // Review-цикл (2026-05-29): reject-DM по Flex шлёт startapp=flex_<id>.
  // Ведём на деталь заказа — там баннер отказа + CTA «Редактировать и
  // отправить повторно». Без этой ветки нотификация открывала просто home,
  // и юзер, не найдя заказ, создавал дубль через пустую форму.
  if (startParam.startsWith("flex_")) {
    const orderId = startParam.slice("flex_".length);
    if (orderId) nav(`/orders/${orderId}`, { replace: true });
    return;
  }
  // bug H13: reject-DM по Shop шлёт startapp=shop_<id> (bot.ts). Без этой ветки
  // кнопка в уведомлении об отказе открывала home, а не экран заказа с баннером
  // отказа и CTA «Редактировать корзину и отправить повторно».
  if (startParam.startsWith("shop_")) {
    const orderId = startParam.slice("shop_".length);
    if (orderId) nav(`/shop/order/${orderId}`, { replace: true });
    return;
  }
  if (startParam.startsWith("order_")) {
    const orderId = startParam.slice("order_".length);
    if (orderId) nav(`/orders/${orderId}`, { replace: true });
  }
}

export default function App() {
  const nav = useNavigate();

  useEffect(() => {
    initTelegram();
    void handleStartParam(nav);
  }, []);

  return (
    <Suspense fallback={<ScreenFallback />}>
      <Routes>
        <Route path="/welcome" element={<WelcomeScreen />} />
        <Route path="/onboarding" element={<OnboardingScreen />} />
        <Route path="/" element={<AppGate />}>
          <Route index element={<HomeScreen />} />
          <Route path="orders" element={<OrdersScreen />} />
          <Route path="profile" element={<ProfileScreen />} />
          <Route path="profile/edit" element={<ProfileEditScreen />} />
          <Route path="profile/qr" element={<MyQrScreen />} />
          <Route path="new-parcel" element={<FlexParcelFormScreen />} />
          <Route path="new-parcel-cis" element={<FlexCisFormScreen />} />
          <Route path="templates" element={<TemplatesListScreen />} />
          <Route path="orders/:id/edit-recipient" element={<PreorderFillScreen />} />
          <Route path="orders/:id" element={<OrderDetailScreen />} />
          <Route path="orders/:id/attachments" element={<FlexAttachmentsScreen />} />
          <Route path="shop" element={<ShopCatalogScreen />} />
          <Route path="shop/cat/:key" element={<ShopCategoryScreen />} />
          <Route path="shop/cart" element={<ShopCartScreen />} />
          <Route path="shop/manager-handoff" element={<ShopManagerHandoffScreen />} />
          {/* MVP упаковки (2026-05-15): checkout-flow временно отложен. Старые
              экраны (Checkout/Payment/Delivery) остаются в коде, но недоступны
              из UI. Раскомментировать когда вернёмся к полной оплате в Mini App. */}
          <Route path="shop/checkout" element={<ShopCheckoutScreen />} />
          <Route path="shop/order/:id" element={<ShopOrderScreen />} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}
